var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/payments.ts
import { A } from "@adornis/base/env-info.js";
import { Float, ID, Int } from "@adornis/baseql/baseqlTypes.js";
import { Arg, Entity, Field, Mutation, Query, Subscription } from "@adornis/baseql/decorators.js";
import { AdornisEntity } from "@adornis/baseql/entities/adornisEntity.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { getCollectionHandle, getRawCollection } from "@adornis/baseql/server/collections.js";
import { context, publishFilter } from "@adornis/baseql/server/context.js";
import { runInServerContext } from "@adornis/baseql/server/server.js";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration.js";
import { goTo } from "@adornis/router/client/open-href.js";
import { Versioned } from "@adornis/versioned-entity/db/versioned-mongo-entity.js";
import { DateTime } from "luxon";
import { from } from "rxjs";
import { switchMap } from "rxjs/operators";
import { getSessionUrlForNewPaymentInstance } from "../server/checkout.js";
import { cancelPayment, refundPayment } from "../server/payment.js";
import { ChosenProduct } from "./chosenProduct.js";
import { Invoice } from "./invoice.js";
import { PaymentError } from "./paymentError.js";
import { ProcessorInfo } from "./processorInfo.js";
import { UserReference } from "./userReference.js";
var PayOptions = class extends AdornisEntity {
  constructor(doc) {
    super(doc);
  }
};
PayOptions._class = "PayOptions";
__decorateClass([
  Field((type) => String)
], PayOptions.prototype, "successURL", 2);
__decorateClass([
  Field((type) => String)
], PayOptions.prototype, "cancelURL", 2);
__decorateClass([
  Field((type) => String)
], PayOptions.prototype, "scope", 2);
__decorateClass([
  Field((type) => [String])
], PayOptions.prototype, "paymentMethods", 2);
__decorateClass([
  Field((type) => Boolean)
], PayOptions.prototype, "createInvoice", 2);
__decorateClass([
  Field((type) => Boolean)
], PayOptions.prototype, "savePaymentMethod", 2);
__decorateClass([
  Field((type) => Boolean)
], PayOptions.prototype, "automaticTax", 2);
__decorateClass([
  Field((type) => Boolean)
], PayOptions.prototype, "collectTaxID", 2);
__decorateClass([
  Field((type) => Boolean)
], PayOptions.prototype, "requireBillingAddress", 2);
__decorateClass([
  Field((type) => String)
], PayOptions.prototype, "sellerID", 2);
PayOptions = __decorateClass([
  Entity()
], PayOptions);
var StatusUpdate = class extends AdornisEntity {
  constructor(doc) {
    super(doc);
  }
  toString() {
    if (this.has("status") && this.has("ts"))
      return `${this.status}: ${this.ts.toLocaleString(DateTime.DATETIME_SHORT)}`;
    return JSON.stringify(this.toJSON());
  }
};
StatusUpdate._class = "StatusUpdate";
__decorateClass([
  Field((type) => ID, { default: (v) => v ?? A.getGloballyUniqueID() })
], StatusUpdate.prototype, "_id", 2);
__decorateClass([
  Field((type) => String)
], StatusUpdate.prototype, "status", 2);
__decorateClass([
  Field((type) => DateTime, { default: (v) => v ?? DateTime.now() })
], StatusUpdate.prototype, "ts", 2);
StatusUpdate = __decorateClass([
  Entity()
], StatusUpdate);
var paymentVersionCollection = "payment-versions";
var Payment = class extends MongoEntity {
  static get allFields() {
    return selectionSet(() => this, 2, ["statusUpdates"]);
  }
  constructor(doc) {
    super(doc);
  }
  /**
   * create a link which can be used by the user to approve the payment of this paymentInstance.
   *
   * This creates the paymentInstance in the database with the `'issued'` status.
   * The user has to visit the returned link and approve or cancel the purchase to update the status to `'paid'` or `'cancelled'`.
   *
   * If the payment is not paid yet, it can also be cancelled by calling `cancelPayment`.
   *
   * You have to provide the products which will be associated with this payment
   * as well as the redirect links the user will be sent to after completing or canceling the purchase.
   *
   * Default successURL is `/product/success`
   *
   * Default cancelURL is `/product/cancel`
   *
   * _WIP after here_
   *
   * You may also provide a sellerID referencing a set of MarketplaceData to send this payment to a specific seller
   */
  async createPaymentLink({
    products,
    mode = "payment",
    scope,
    paymentMethods = ["card", "paypal"],
    successURL = "/product/success",
    cancelURL = "/product/cancel",
    customerID,
    sellerID,
    createInvoice,
    savePaymentMethod,
    automaticTax,
    collectTaxID,
    requireBillingAddress
  }) {
    assertMode(mode, products);
    const parsedProducts = products.map((product) => {
      const referencedProduct = product.product.toJSON();
      referencedProduct.reference = product.product._id;
      referencedProduct._id = A.getGloballyUniqueID() + "_copied_" + A.getGloballyUniqueID();
      return new ChosenProduct({
        amount: product.amount,
        product: referencedProduct,
        priceID: product.priceID
      });
    });
    this.mode = mode;
    this.processorInfo.stripeCustomerID = customerID ?? null;
    const stripeLink = await Payment.payPaymentWithStripe(
      this,
      parsedProducts,
      new PayOptions({
        successURL,
        cancelURL,
        scope,
        paymentMethods,
        sellerID,
        createInvoice,
        savePaymentMethod,
        automaticTax,
        collectTaxID,
        requireBillingAddress
      })
    )();
    if (!stripeLink)
      throw new Error("no link to stripe session");
    return stripeLink;
  }
  /**
   * This is a shortcut for createPaymentLink -> goTo (routing)
   *
   * redirect the user to the payment url of this paymentInstance.
   *
   * You have to provide the products which will be associated with this payment
   * as well as the redirect links the user will be sent to after completing or canceling the purchase.
   *
   * Default successURL is `/product/success`
   *
   * Default cancelURL is `/product/cancel`
   *
   * _WIP after here_
   *
   * You may also provide a sellerID referencing a set of MarketplaceData to send this payment to a specific seller
   */
  async pay(options) {
    const link = await this.createPaymentLink(options);
    goTo(link);
  }
  static async invoicePaid(paymentInstance, invoiceID) {
  }
  static payPaymentWithStripe(paymentInstance, products, payOptions) {
    return async (gqlFields) => {
      if (context.serverContext)
        throw new Error("the server cannot buy anything");
      const userID = context.userID;
      return runInServerContext(
        () => getSessionUrlForNewPaymentInstance({
          scope: payOptions.scope ?? "default",
          paymentInstance,
          products,
          userID,
          paymentMethods: payOptions.paymentMethods,
          successURL: payOptions.successURL,
          cancelURL: payOptions.cancelURL,
          createInvoice: payOptions.createInvoice,
          savePaymentMethod: payOptions.savePaymentMethod,
          automaticTax: payOptions.automaticTax,
          collectTaxID: payOptions.collectTaxID,
          requireBillingAddress: payOptions.requireBillingAddress
        })
      );
    };
  }
  static cancelPayment(paymentId, cancelReason) {
    return async (gqlFields) => {
      if (context.serverContext)
        throw new Error("the server cannot refund anything");
      const payment = await this.getByID(paymentId)({
        _id: 1,
        _class: 1,
        scope: 1,
        processorInfo: ProcessorInfo.allFields
      });
      if (!payment)
        throw new Error("Payment not found");
      return runInServerContext(() => cancelPayment({ paymentInstance: payment, reason: cancelReason }));
    };
  }
  static refundPayment(paymentId, refundReason, refund) {
    return async (gqlFields) => {
      if (context.serverContext)
        throw new Error("the server cannot refund anything");
      const payment = await this.getByID(paymentId)({
        _id: 1,
        _class: 1,
        scope: 1,
        processorInfo: ProcessorInfo.allFields,
        status: 1
      });
      if (!payment)
        throw new Error("Payment not found");
      return runInServerContext(() => refundPayment({ paymentInstance: payment, reason: refundReason, refund }));
    };
  }
  static getByUserID(userID) {
    return async (gqlFields) => {
      const filter = await publishFilter(this._class);
      const selector = { "userReference.userID": userID };
      return (await (await getRawCollection(this._collectionName)).find(filter ? { $and: [filter, selector] } : selector, { sort: { createdAt: -1 } }).toArray()).map((p) => new this(p));
    };
  }
  static getSuccessfulPayments(userID) {
    return async (gqlFields) => {
      const filter = await publishFilter(this._class);
      const selector = {
        "userReference.userID": userID,
        status: "paid"
      };
      return (await (await getRawCollection(this._collectionName)).find(filter ? { $and: [filter, selector] } : selector).toArray()).map((p) => new this(p));
    };
  }
  static subscribeSuccessfulPayments(userID) {
    return (gqlFields) => {
      const handle = getCollectionHandle(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap((filter) => {
          const selector = {
            "userReference.userID": userID,
            status: "paid"
          };
          return handle.watchQuery(filter ? { $and: [filter, selector] } : selector);
        })
      );
    };
  }
};
Payment._class = "Payment";
Payment._collectionName = "payments";
__decorateClass([
  Field((type) => String)
], Payment.prototype, "mode", 2);
__decorateClass([
  Field((type) => String)
], Payment.prototype, "status", 2);
__decorateClass([
  Field((type) => [ChosenProduct])
], Payment.prototype, "products", 2);
__decorateClass([
  Field((type) => [Invoice], { default: (v) => v ?? [] })
], Payment.prototype, "invoices", 2);
__decorateClass([
  Field((type) => UserReference, { default: (v) => v ?? new UserReference({}) })
], Payment.prototype, "userReference", 2);
__decorateClass([
  Field((type) => DateTime)
], Payment.prototype, "createdAt", 2);
__decorateClass([
  Field((type) => String)
], Payment.prototype, "paymentType", 2);
__decorateClass([
  Field((type) => String)
], Payment.prototype, "scope", 2);
__decorateClass([
  Field((type) => String)
], Payment.prototype, "cancelReason", 2);
__decorateClass([
  Field((type) => String)
], Payment.prototype, "additionalInfo", 2);
__decorateClass([
  Field((type) => Int)
], Payment.prototype, "amount", 2);
__decorateClass([
  Field((type) => Float)
], Payment.prototype, "tax", 2);
__decorateClass([
  Field((type) => ProcessorInfo, { default: (v) => v ?? new ProcessorInfo({}) })
], Payment.prototype, "processorInfo", 2);
__decorateClass([
  Field((type) => [PaymentError], { default: (v) => v ?? [] })
], Payment.prototype, "errors", 2);
__decorateClass([
  Field((type) => [StatusUpdate], {
    resolve: function() {
      return async () => {
        const col = await getRawCollection(paymentVersionCollection);
        return col.aggregate([
          { $match: { "patches.path": /status/ } },
          { $unwind: "$patches" },
          { $match: { "patches.path": /status/, "patches.op": { $ne: "test" } } },
          { $project: { _id: 1, status: "$patches.value", ts: "$timestamp", _class: StatusUpdate._class } }
        ]).toArray();
      };
    }
  })
], Payment.prototype, "statusUpdates", 2);
__decorateClass([
  Mutation((type) => String),
  __decorateParam(0, Arg("paymentInstance", (type) => MongoEntity)),
  __decorateParam(1, Arg("products", (type) => [ChosenProduct])),
  __decorateParam(2, Arg("payOptions", (type) => PayOptions))
], Payment, "payPaymentWithStripe", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("paymentId", (type) => String)),
  __decorateParam(1, Arg("cancelReason", (type) => String))
], Payment, "cancelPayment", 1);
__decorateClass([
  Mutation((type) => Boolean),
  __decorateParam(0, Arg("paymentId", (type) => String)),
  __decorateParam(1, Arg("refundReason", (type) => String)),
  __decorateParam(2, Arg("refund", (type) => Int))
], Payment, "refundPayment", 1);
__decorateClass([
  Query((type) => [Payment]),
  __decorateParam(0, Arg("userID", (type) => String))
], Payment, "getByUserID", 1);
__decorateClass([
  Query((type) => [Payment]),
  __decorateParam(0, Arg("userID", (type) => String))
], Payment, "getSuccessfulPayments", 1);
__decorateClass([
  Subscription((type) => [Payment]),
  __decorateParam(0, Arg("userID", (type) => String))
], Payment, "subscribeSuccessfulPayments", 1);
Payment = __decorateClass([
  Versioned({ versionCollection: paymentVersionCollection }),
  Entity()
], Payment);
var assertMode = (mode, products) => {
  if (mode === "payment" && products.some((product) => product.product.prices.find((price) => price._id === product.priceID)?.interval))
    throw new Error('You can only create a session in "payment" mode if none of the selected prices are recurring.');
  if (mode === "subscription" && !products.every((product) => product.product.prices.find((price) => price._id === product.priceID)?.interval))
    throw new Error('You can only create a session in "subscription" mode if all your selected prices are recurring.');
};
export {
  PayOptions,
  Payment,
  StatusUpdate,
  paymentVersionCollection
};
